<template>
  <CreateEditBidderPartnerModal
    v-if="showCreateEditBidderPartnerModal"
    :type="typePartner"
    @closeModal="closeCreateEditBidderPartnerModal"
  />
  <CreateEditResponsibleModal
    v-if="showCreateEditResponsibleModal"
    :bidderPartnerId="partner.bidderPartnerId"
    @closeModal="closeCreateEditResponsibleModal"
  />
  <div id="modalCreateEditPartnerProject" class="modal fade" tabindex="-1">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Adicionar Parceiro</h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            title="Fechar"
            @click="$emit('closeModal', false)"
          />
        </div>
        <form @submit.prevent="save">
          <div class="modal-body">
            <div class="row g-3">
              <div class="col-sm-12">
                <label class="form-label">
                  Parceiro:
                  <span class="text-danger">*</span>
                </label>
                <select
                  class="form-select"
                  :class="{
                    'is-invalid': v$.partner.bidderPartnerId.$errors.length,
                  }"
                  v-model="v$.partner.bidderPartnerId.$model"
                >
                  <option value="">Selecione um Parceiro</option>
                  <option
                    v-for="bidderPartner in biddersPartners"
                    :key="bidderPartner.id"
                    :value="bidderPartner.id"
                    :title="`${bidderPartner.name} - ${bidderPartner.cnpj}`"
                  >
                    {{ bidderPartner.name }} - {{ bidderPartner.cnpj }}
                  </option>
                </select>
                <div class="row">
                  <div class="col-sm-6">
                    <span
                      v-if="v$.partner.bidderPartnerId.required.$invalid"
                      class="invalid-feedback"
                    >
                      O campo é obrigatório
                    </span>
                  </div>
                  <div class="col-sm-6">
                    <div
                      class="form-text text-primary text-end cursor-pointer"
                      title="Cadastrar novo parceiro"
                      @click="createBidderPartner()"
                    >
                      Cadastrar novo parceiro
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="partner.bidderPartnerId" class="col-sm-12">
                <label class="form-label">
                  Responsável:
                  <span class="text-danger">*</span>
                </label>
                <select
                  class="form-select"
                  :class="{
                    'is-invalid': v$.partner.responsibleId.$errors.length,
                  }"
                  v-model="v$.partner.responsibleId.$model"
                >
                  <option value="">Selecione um Responsável</option>
                  <option
                    v-for="responsible in responsibles"
                    :key="responsible.id"
                    :value="responsible.id"
                    :title="`${responsible.name} - ${responsible.function} | ${responsible.role}`"
                  >
                    {{ responsible.name }} - {{ responsible.function }} |
                    {{ responsible.role }}
                  </option>
                </select>
                <div class="row">
                  <div class="col-sm-6">
                    <span
                      v-if="v$.partner.responsibleId.required.$invalid"
                      class="invalid-feedback"
                    >
                      O campo é obrigatório
                    </span>
                  </div>
                  <div class="col-sm-6">
                    <div
                      class="form-text text-primary text-end cursor-pointer"
                      title="Cadastrar novo responsável"
                      @click="createResponsible()"
                    >
                      Cadastrar novo responsável
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer border-top-0">
            <button
              type="button"
              class="btn btn-sm btn-link"
              data-bs-dismiss="modal"
              title="Cancelar"
              @click="$emit('closeModal', false)"
            >
              Cancelar
            </button>
            <button
              type="submit"
              class="btn btn-sm btn-primary"
              title="Adicionar"
              :disabled="v$.$invalid || disableButton"
            >
              <span v-if="disableButton">
                <i class="ph-spinner spinner" />
              </span>
              <span v-else>Adicionar</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { showModal, hideModal } from '@/utils/modal'
import CreateEditBidderPartnerModal from '@/components/biddersPartners/CreateEditBidderPartnerModal.vue'
import CreateEditResponsibleModal from '@/components/biddersPartners/CreateEditResponsibleModal.vue'
import TypeBidderPartnerEnum from '@/enum/typeBidderPartnerEnum'

export default {
  name: 'CreateEditPartnerProjectModal',

  components: {
    CreateEditBidderPartnerModal,
    CreateEditResponsibleModal,
  },

  props: {
    projectId: {
      type: String,
      required: true,
    },
    projectPartnerId: {
      type: String,
      default: null,
    },
  },

  setup() {
    return { v$: useVuelidate() }
  },

  emits: ['closeModal'],

  data() {
    return {
      biddersPartners: [],
      responsibles: [],
      showCreateEditBidderPartnerModal: false,
      showCreateEditResponsibleModal: false,
      partner: {
        bidderPartnerId: '',
        responsibleId: '',
      },
      disableButton: false,
    }
  },

  computed: {
    typePartner() {
      return TypeBidderPartnerEnum.PARTNER
    },
  },

  watch: {
    'partner.bidderPartnerId': {
      handler(newVal, oldVal) {
        this.partner.responsibleId = ''
        this.responsibles = []
        if (newVal) {
          this.getResponsibles()
        }
      },
      immediate: true,
    },
  },

  methods: {
    async getBiddersPartners() {
      try {
        this.biddersPartners = await this.$store.dispatch(
          'getProjectPartnersUnrelated',
          {
            projectId: this.projectId,
            projectPartnerId: this.projectPartnerId,
          },
        )
      } catch (error) {
        if (error && error.message) {
          this.$root.$refs.notification.warning(error.message)
        } else {
          this.$root.$refs.notification.error(
            'Não foi possível se conectar ao servidor.',
          )
        }
        this.$emit('closeModal', false)
      }
    },

    async getResponsibles() {
      try {
        this.responsibles = await this.$store.dispatch('getResponsibles', {
          bidderPartnerId: this.partner.bidderPartnerId,
          active: true,
        })
      } catch (error) {
        if (error && error.message) {
          this.$root.$refs.notification.warning(error.message)
        } else {
          this.$root.$refs.notification.error(
            'Não foi possível se conectar ao servidor.',
          )
        }
      }
    },

    createBidderPartner() {
      hideModal()
      this.showCreateEditBidderPartnerModal = true
    },

    createResponsible() {
      hideModal()
      this.showCreateEditResponsibleModal = true
    },

    closeCreateEditBidderPartnerModal(bidderPartnerId) {
      showModal('modalCreateEditPartnerProject')

      if (bidderPartnerId) {
        this.getBiddersPartners()
        this.partner.bidderPartnerId = bidderPartnerId
      }
      this.showCreateEditBidderPartnerModal = false
    },

    closeCreateEditResponsibleModal(responsibleId) {
      showModal('modalCreateEditPartnerProject')

      if (responsibleId) {
        this.getResponsibles()
        this.partner.responsibleId = responsibleId
      }
      this.showCreateEditResponsibleModal = false
    },

    async save() {
      if (!this.v$.$invalid) {
        this.disableButton = true
        try {
          let response = null

          if (this.projectPartnerId) {
            response = await this.$store.dispatch('updateProjectPartner', {
              projectId: this.projectId,
              projectPartnerId: this.projectPartnerId,
              partner: this.partner,
            })
          } else {
            response = await this.$store.dispatch('saveProjectPartner', {
              projectId: this.projectId,
              partner: this.partner,
            })
          }

          hideModal()
          this.$emit('closeModal', true)
          this.$root.$refs.notification.success(response.message)
        } catch (error) {
          this.disableButton = false
          if (error && error.message) {
            this.$root.$refs.notification.warning(error.message)
          } else {
            this.$root.$refs.notification.error(
              'Não foi possível se conectar ao servidor.',
            )
          }
        }
      }
    },
  },

  validations() {
    return {
      partner: {
        bidderPartnerId: {
          required,
        },
        responsibleId: {
          required,
        },
      },
    }
  },

  mounted() {
    this.getBiddersPartners()
    showModal('modalCreateEditPartnerProject')
  },
}
</script>

<style scoped>
.is-invalid ~ div > div .invalid-feedback {
  display: block;
}
</style>
